"use client";

import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import { type BlokProps, StoryblokComponent } from "@trydig/storyblok";
import { clsx } from "@trydig/ui";
import { BlokWrap } from "@/components/bloks/BlokWrap";
import { Typography } from "@/components/ui/Typography";
import type { LinkStoryblok, MegaMenuSectionStoryblok } from "@/generated/sb/sb-types";

const MAX_ITEMS_PER_COLUMN = 8;

const chunk = <T,>(items: T[], size: number): T[][] => {
	if (items.length === 0) return [[]];
	const out: T[][] = [];
	for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
	return out;
};

export const MegaMenuSectionBlok = ({
	blok,
	preview,
	index,
}: BlokProps<MegaMenuSectionStoryblok> & {
	index?: number;
}) => {
	const isFirstSection = index === 0;
	const columns = chunk(blok.links ?? [], MAX_ITEMS_PER_COLUMN);

	const Column = ({ links, showHeader }: { links: LinkStoryblok[]; showHeader: boolean }) => (
		<div className="flex flex-col gap-4 min-w-[156px]">
			{showHeader ? (
				<Typography variant="label-sm-reg" className="text-text-sand-text-4 uppercase">
					{blok.header}
				</Typography>
			) : (
				// Invisible header placeholder so overflow column items align with
				// the heading column's items.
				<Typography variant="label-sm-reg" className="invisible" aria-hidden>
					&nbsp;
				</Typography>
			)}
			<div className="flex flex-col gap-2">
				{links.map((link, i) => (
					<NavigationMenu.Link key={i} asChild>
						<div>
							<StoryblokComponent
								blok={link}
								preview={preview}
								className={clsx(
									"text-text-dark-text-1",
									isFirstSection
										? "font-display py-[6.5px] leading-[125%] text-[18px]"
										: "text-xs leading-[150%] py-0.5",
								)}
								unstyled
								rightIcon={false}
							/>
						</div>
					</NavigationMenu.Link>
				))}
			</div>
		</div>
	);

	// A section owns all of its columns: the heading column plus any overflow
	// continuation columns sit side by side in one flex row that never wraps. So
	// a category that exceeds MAX_ITEMS_PER_COLUMN grows a new column *next to*
	// itself instead of spilling onto a new row (ELL-187). The divider lives on
	// the section (skipping the first), not on individual columns.
	return (
		<BlokWrap
			editable={blok}
			as="div"
			className={clsx(
				"flex items-start gap-x-(--layout-gap)",
				!isFirstSection && "border-l border-neutral-black-30 pl-4",
			)}
		>
			{columns.map((columnLinks, i) => (
				<Column key={i} links={columnLinks} showHeader={i === 0} />
			))}
		</BlokWrap>
	);
};
